import { Fragment } from "react";

import { DialogTitle, DialogContent, Grid, Typography, DialogActions, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";

import { DateTime } from "luxon";

import LitcoTextField from "../../../UI/LitcoTextField";
import LitcoRadioField from "../../../UI/LitcoRadioField";
import LitcoLogo from "../../../../images/LitcoLogo";

export default function OtherOccupantsForm(props) {
  const { otherOccupants, onSubmit, onInfoChangeHandler, onDatePickerOpen, loading, validInfo, onClose } = props;

  return (
    <form onSubmit={onSubmit} method="POST" id="otherOccupantsDetailsForm">
      <DialogTitle title="Thank You" />

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid
              container
              justifyContent={{ xs: "center", sm: undefined }}
              alignItems={{ xs: undefined, sm: "center" }}
              rowSpacing={1}
              columnSpacing={2}
            >
              <Grid item display={{ xs: "block", sm: "none" }}>
                <LitcoLogo mobileSize={20} desktopSize={25} opacity={0.4} />
              </Grid>
              <Grid item xs={12} sm>
                <Typography variant="body1" gutterBottom>
                  Thank you for booking your appointment with Litco Law! We will be in contact with you shortly to
                  confirm your consult. Since you have more people involved in the accident, would you mind sending us
                  some additional information to make your consult even faster?
                </Typography>
              </Grid>

              <Grid item display={{ xs: "none", sm: "block" }}>
                <LitcoLogo mobileSize={20} desktopSize={25} opacity={0.4} />
              </Grid>
            </Grid>
          </Grid>

          {Object.keys(otherOccupants).map((otherOccupant, index) => {
            const sameAddress = otherOccupants[otherOccupant].sameAddress;
            const isMinor = otherOccupants[otherOccupant].isMinor;
            const driverOrPassenger = otherOccupants[otherOccupant].driverOrPassenger;
            const dateTimeDateOfBirth = otherOccupants[otherOccupant].dateOfBirth
              ? DateTime.fromISO(otherOccupants[otherOccupant].dateOfBirth)
              : "";

            return (
              <Fragment key={index}>
                <Grid item xs={12} sx={{ background: "#000" }}>
                  <Typography variant="subtitle2" component="div" color="white">
                    {otherOccupant.toUpperCase()}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LitcoTextField
                    label="Legal First Name(s)"
                    name="firstName"
                    required
                    value={otherOccupants[otherOccupant].firstName}
                    onChange={(event) => {
                      onInfoChangeHandler(otherOccupant, event);
                    }}
                    maxLength={32}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LitcoTextField
                    label="Legal Middle Name(s)"
                    name="middleName"
                    value={otherOccupants[otherOccupant].middleName}
                    onChange={(event) => {
                      onInfoChangeHandler(otherOccupant, event);
                    }}
                    maxLength={32}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <LitcoTextField
                    label="Legal Last Name(s)"
                    name="lastName"
                    required
                    value={otherOccupants[otherOccupant].lastName}
                    onChange={(event) => {
                      onInfoChangeHandler(otherOccupant, event);
                    }}
                    maxLength={32}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Grid container alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <LitcoRadioField
                        label="Person lives in the same address as you?"
                        name="sameAddress"
                        value={sameAddress}
                        options={[{ label: "No" }, { label: "Yes" }]}
                        onChange={onInfoChangeHandler}
                        onChangeExtraParameters={otherOccupant}
                        required
                      />
                    </Grid>

                    {sameAddress && sameAddress === "no" && (
                      <Grid item xs={12} sm>
                        <LitcoTextField
                          label="Address"
                          name="address"
                          required={sameAddress === "no"}
                          value={otherOccupants[otherOccupant].address}
                          onChange={(event) => {
                            onInfoChangeHandler(otherOccupant, event);
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <LitcoRadioField
                        label="Is the person under 18 years of age?"
                        name="isMinor"
                        value={isMinor}
                        options={[{ label: "No" }, { label: "Yes" }]}
                        onChange={onInfoChangeHandler}
                        onChangeExtraParameters={otherOccupant}
                        required
                      />
                    </Grid>

                    {isMinor === "yes" && (
                      <Grid item xs={12} sm>
                        <LitcoTextField
                          label="Minor Date of Birth"
                          name="dateOfBirth"
                          value={dateTimeDateOfBirth ? dateTimeDateOfBirth.toLocaleString(DateTime.DATE_MED) : ""}
                          required={isMinor === "yes"}
                          onFocus={() => onDatePickerOpen(otherOccupant)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <LitcoRadioField
                        label="Was the person the driver or passenger?"
                        name="driverOrPassenger"
                        value={driverOrPassenger}
                        options={[{ label: "Driver" }, { label: "Passenger" }, { label: "Other" }]}
                        onChange={onInfoChangeHandler}
                        onChangeExtraParameters={otherOccupant}
                        required
                      />
                    </Grid>

                    {driverOrPassenger && driverOrPassenger === "other" && (
                      <Grid item xs={12} sm>
                        <LitcoTextField
                          label="Please specify"
                          name="driverPassengerOther"
                          required={driverOrPassenger === "other"}
                          value={otherOccupants[otherOccupant].driverPassengerOther}
                          onChange={(event) => {
                            onInfoChangeHandler(otherOccupant, event);
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <LitcoRadioField
                    label="Will this person attend the consult?"
                    name="attendingConsult"
                    value={otherOccupants[otherOccupant].attendingConsult}
                    options={[{ label: "No" }, { label: "Yes" }]}
                    onChange={onInfoChangeHandler}
                    onChangeExtraParameters={otherOccupant}
                    required
                  />
                </Grid>
              </Fragment>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between" p={1}>
          <Button onClick={onClose} size="small">
            Close
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            startIcon={<SendIcon />}
            disabled={!validInfo}
            type="submit"
          >
            Submit
          </LoadingButton>
        </Grid>
      </DialogActions>
    </form>
  );
}
